import React, { useState, useEffect } from "react";
import {
	MapPinIcon,
	PencilIcon,
	CheckCircleIcon,
} from "@heroicons/react/20/solid";
import Input from "../../../shared/components/FormElements/Input";
import PracticeConfirmation from "./PracticeConfirmation";
import { PencilSquareIcon } from "@heroicons/react/16/solid";

const AddressSelector = ({
	formikProps,
	addressSuggestions = [],
	prefillAddress,
}) => {
	const [selectedAddress, setSelectedAddress] = useState(null);
	const [showManualInput, setShowManualInput] = useState(false);
	const [isConfirmed, setIsConfirmed] = useState(false);
	const { values } = formikProps;

	useEffect(() => {
		// Auto-select if there's exactly one suggestion
		if (addressSuggestions.length === 1) {
			handleSelectAddress(addressSuggestions[0]);
		}
		// Auto-fill if we have prefill data
		else if (prefillAddress && addressSuggestions.length === 0) {
			const street =
				prefillAddress.address || prefillAddress.street || "";
			const city = prefillAddress.city || "";
			const postalCode =
				prefillAddress.postalCode || prefillAddress.postal_code || "";
			const country = prefillAddress.country || "Nederland";

			setSelectedAddress({
				name: prefillAddress.name || "Vooringevuld adres",
				address: {
					street: street,
					city: city,
					postalCode: postalCode,
					country: country,
				},
			});

			formikProps.setFieldValue("address", street);
			formikProps.setFieldValue("city", city);
			formikProps.setFieldValue("postalCode", postalCode);
			formikProps.setFieldValue("country", country);

			console.log("Prefill address set:", {
				street,
				city,
				postalCode,
				country,
			});
		}
		// Show manual input if no suggestions
		else if (addressSuggestions.length === 0 && !prefillAddress) {
			setShowManualInput(true);
		}
	}, [addressSuggestions, prefillAddress]);

	const handleSelectAddress = (suggestion, skipConfirmation = false) => {
		setSelectedAddress(suggestion);

		// Update formik values with proper validation
		const street =
			suggestion.address.street || suggestion.address.line1 || "";
		const city = suggestion.address.city || "";
		const postalCode =
			suggestion.address.postalCode ||
			suggestion.address.postal_code ||
			"";
		const country = suggestion.address.country || "Nederland";

		formikProps.setFieldValue("address", street);
		formikProps.setFieldValue("city", city);
		formikProps.setFieldValue("postalCode", postalCode);
		formikProps.setFieldValue("country", country);

		console.log("Address selected:", { street, city, postalCode, country });

		setShowManualInput(false);

		// If user actively selected from multiple suggestions, skip the "Klopt dit?" step
		if (skipConfirmation || addressSuggestions.length > 1) {
			setIsConfirmed(true);
		} else {
			setIsConfirmed(false);
		}
	};

	const handleManualInput = () => {
		setSelectedAddress(null);
		setShowManualInput(true);
		setIsConfirmed(false);
	};

	const handleConfirm = () => {
		setIsConfirmed(true);
	};

	// Multiple suggestions view
	if (addressSuggestions.length > 1 && !selectedAddress && !showManualInput) {
		return (
			<div className="space-y-4">
				<div className="text-center">
					<p className="text-lg font-medium text-gray-900 mb-4">
						Staat je adres hiertussen?
					</p>
				</div>

				<div className="space-y-3">
					{addressSuggestions.map((suggestion, index) => (
						<button
							key={index}
							type="button"
							onClick={() =>
								handleSelectAddress(suggestion, true)
							}
							className="w-full text-left p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 group"
						>
							<div className="flex items-start space-x-3">
								<div className="flex-1">
									<div className="font-medium text-gray-900 group-hover:text-purple-900">
										{suggestion.name}
									</div>
									<div className="text-sm text-gray-600 group-hover:text-purple-700 mt-1">
										{suggestion.address.street &&
											`${suggestion.address.street}, `}
										{suggestion.address.postalCode}{" "}
										{suggestion.address.city}
									</div>
								</div>
							</div>
						</button>
					))}
				</div>

				<div className="text-center">
					<button
						type="button"
						onClick={handleManualInput}
						className="text-sm text-gray-500 hover:text-gray-700 underline"
					>
						Mijn adres staat er niet tussen - handmatig invullen
					</button>
				</div>
			</div>
		);
	}

	// Selected address view - confirmation flow
	if (selectedAddress && !showManualInput && !isConfirmed) {
		return (
			<div className="space-y-2">
				<div className="flex items-start gap-1 text-sm  text-green-600">
					<CheckCircleIcon className="size-4 flex-shrink-0 mt-0.5" />

					<h3 className="font-semibold text-green-700">
						Praktijk gevonden!
					</h3>
				</div>
				<div className="bg-slate-50 rounded-xl w-fit border border-slate-200 p-4">
					<div className="text-sm text-slate-900/90 space-y-0.5">
						<div className="font-medium mb-1 text-slate-900">
							{selectedAddress.name}
						</div>
						<div>{selectedAddress.address.street}</div>
						<div>
							{selectedAddress.address.postalCode}{" "}
							{selectedAddress.address.city}
						</div>
					</div>
				</div>
				<button
					type="button"
					onClick={handleManualInput}
					className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
				>
					<PencilSquareIcon className="size-4" />
					Handmatig aanpassen
				</button>
			</div>
		);
	}

	// Confirmed address view - show summary
	if (selectedAddress && isConfirmed) {
		return (
			<PracticeConfirmation
				formikProps={formikProps}
				selectedAddress={selectedAddress}
				onEdit={handleManualInput}
			/>
		);
	}

	// Manual input view
	return (
		<div className="space-y-4">
			<h3 className="text-lg font-semibold text-gray-900">
				Factuuradres
			</h3>

			<div className="space-y-4">
				<Input
					name="address"
					label="Adres"
					placeholder="Straatnaam 123"
					type="text"
					width="full"
				/>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<Input
						name="postalCode"
						label="Postcode"
						placeholder="1234 AB"
						type="text"
						width="full"
					/>

					<Input
						name="city"
						label="Stad"
						placeholder="Amsterdam"
						type="text"
						width="full"
					/>
				</div>

			</div>

		</div>
	);
};

export default AddressSelector;
